import type {
  AutocompleteField,
  FilterItem,
} from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import type {
  CompanyType,
  ServiceDeskLicensesSearchRequest,
  VendorLicensesSearchRequest,
} from "@services/licensing/types";
import type { TFunction } from "i18next";

export const LICENSES_FIELDS = Object.freeze({
  REQUESTED_ID: "requestedId",
  LICENSE_TYPE: "licenseType",
  COMPANY_TYPE: "companyType",
  NATURE_OF_BUSINESS: "natureOfBusiness",
  EXPIRED_DATE: "expiredDate",
  DATE_SUBMITTED: "submittedDate",
  TRADE_LICENSE: "tradeLicense",
  STATUS: "status",
});
export const PORT_OPERATOR_LICENSES_FIELDS = Object.freeze({
  REQUESTED_ID: "requestedId",
  COMPANY_NAME: "companyName",
  COMPANY_TYPE: "companyType",
  NATURE_OF_BUSINESS: "natureOfBusiness",
  TRADE_LICENSE: "tradeLicense",
  DATE_SUBMITTED: "dateSubmitted",
  STATUS: "status",
});

export const ACCOUNTS_FIELDS = Object.freeze({
  REQUESTED_ID: "requestedId",
  LICENSE_TYPE: "licenseType",
  COMPANY_NAME: "companyName",
  COMPANY_TYPE: "companyType",
  NATURE_OF_BUSINESS: "natureOfBusiness",
  TRADE_LICENSE: "tradeLicense",
  DATE_SUBMITTED: "dateSubmitted",
  STATUS: "status",
});

export const getCompanyTypeFilterOptions = (companyTypes: CompanyType[]) =>
  companyTypes.map(({ companyTypeDesc }) => ({
    label: companyTypeDesc,
    value: companyTypeDesc,
  }));

export const getCompanyTypeFilterField = (
  label: string,
  companyTypes: CompanyType[],
): AutocompleteField => ({
  label,
  type: "autocomplete",
  options: getCompanyTypeFilterOptions(companyTypes),
});

export type LicenseStatus =
  | "APPROVED"
  | "ACCEPTED"
  | "PENDING"
  | "REJECTED"
  | "EXPIRED"
  | "AWAITING_MANAGING_DIRECTOR_VERIFICATION"
  | "AWAITING_MANAGING_DIRECTOR_APPROVAL"
  | "AWAITING_UPLOAD_RECEIPT"
  | "REJECTED_MANAGING_DIRECTOR_VERIFICATION"
  | "REJECTED_BY_MANAGING_DIRECTOR"
  | string;

export function formatDateTime(dateString?: string | null): string {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

export function buildBodyFromFilters(
  filters: FilterItem[],
): VendorLicensesSearchRequest | ServiceDeskLicensesSearchRequest {
  const body: any = {};

  for (const filter of filters) {
    if (!filter.field || filter.value == null || filter.value === "") {
      continue;
    }

    switch (filter.field) {
      case "refOrderNumber": {
        const parsed = Number(filter.value);

        if (!Number.isNaN(parsed)) {
          body.refOrderNumber = [...(body.refOrderNumber ?? []), parsed];
        }

        break;
      }

      case "createdAt": {
        const value = filter.value as { from?: string | null; to?: string | null };

        if (value.from || value.to) {
          body.createdAt = [
            ...(body.createdAt ?? []),
            {
              from: value.from ? new Date(value.from).toISOString() : null,
              to: value.to ? new Date(value.to).toISOString() : null,
            },
          ];
        }

        break;
      }

      case "status": {
        const value = String(filter.value).trim().toUpperCase();

        body.status = [...(body.status ?? []), value];

        break;
      }

      case "companyType": {
        const value = String(filter.value).trim();

        if (value) {
          body.companyTypes = [...(body.companyTypes ?? []), value];
        }

        break;
      }

      default: {
        const value = String(filter.value).trim();

        if (value) {
          (body as any)[filter.field] = [
            ...(((body as any)[filter.field] as string[] | undefined) ?? []),
            value,
          ];
        }

        break;
      }
    }
  }

  return body;
}

export function formatStatus(status: LicenseStatus | undefined, t: TFunction): string {
  if (!status) return "-";

  const fallbackLabel = status
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1));

  return t(`licensing.status-labels.${status}`, fallbackLabel);
}

export function getLicenseBadgeType(
  status: LicenseStatus | undefined,
): "success" | "warning" | "error" {
  if (!status) return "warning";

  const map: Record<string, "success" | "warning" | "error"> = {
    APPROVED: "success",
    ACCEPTED: "success",
    RENEWED: "success",
    PENDING_RENEWAL: "success",
    PENDING: "warning",
    AWAITING_MANAGING_DIRECTOR_VERIFICATION: "warning",
    AWAITING_MANAGING_DIRECTOR_APPROVAL: "warning",
    AWAITING_UPLOAD_RECEIPT: "warning",
    REJECTED: "error",
    REJECTED_MANAGING_DIRECTOR_VERIFICATION: "error",
    REJECTED_BY_MANAGING_DIRECTOR: "error",
    EXPIRED: "error",
  };

  return map[status] ?? "warning";
}

export function formatLicenseType(input: string): string {
  if (!input) return "-";

  const map: Record<string, string> = {
    VENDOR_LICENSE: "MarHub Access",
    PORT_OPERATOR_LICENSE: "Port Operator License",
  };

  return map[input] ?? input;
}

export function getLicenseTypeUrlValue(input: string): string {
  return input === "VENDOR_LICENSE" ? "MARHUB_ACCESS" : input;
}
